(() => {
  const CONFIG_ELEMENT_ID = "klaviyo-newsletter-config";
  const KLAVIYO_REVISION = "2024-10-15";
  const SUBMITTING_ATTRIBUTE = "data-klaviyo-newsletter-submitting";
  const CONSENT_SELECTOR = "[data-newsletter-consent]";
  const CONSENT_FIELD_SELECTOR = "[data-newsletter-consent-field]";
  const HONEYPOT_SELECTOR = "[data-newsletter-honeypot]";
  const DEFAULT_ERROR_MESSAGE =
    "We could not complete your subscription. Please try again.";

  let cachedConfig;

  function getConfig() {
    if (cachedConfig !== undefined) {
      return cachedConfig;
    }

    const configElement = document.getElementById(CONFIG_ELEMENT_ID);
    if (!configElement?.textContent) {
      cachedConfig = null;
      return cachedConfig;
    }

    try {
      cachedConfig = JSON.parse(configElement.textContent);
    } catch (error) {
      console.error("Klaviyo newsletter sync: invalid config JSON.", error);
      cachedConfig = null;
    }

    return cachedConfig;
  }

  function isSyncEnabled(config) {
    return Boolean(
      config?.companyId &&
      config.companyId !== "PLACEHOLDER" &&
      config.listId &&
      config.listId !== "PLACEHOLDER",
    );
  }

  function getNewsletterTagInput(form) {
    return form.querySelector('input[name="contact[tags]"]');
  }

  function hasNewsletterTag(form) {
    const newsletterTagInput = getNewsletterTagInput(form);
    if (!(newsletterTagInput instanceof HTMLInputElement)) {
      return false;
    }

    return newsletterTagInput.value
      .split(",")
      .map((value) => value.trim().toLowerCase())
      .includes("newsletter");
  }

  function getEmailInput(form) {
    const emailInput = form.querySelector('input[name="contact[email]"]');
    return emailInput instanceof HTMLInputElement ? emailInput : null;
  }

  function isNewsletterForm(form) {
    if (!(form instanceof HTMLFormElement)) {
      return false;
    }

    if (!getEmailInput(form)) {
      return false;
    }

    return (
      form.matches(".newsletter-form") ||
      hasNewsletterTag(form) ||
      form.dataset.klaviyoNewsletterForm === "true"
    );
  }

  function hasBotFieldValue(form) {
    const honeypot = form.querySelector(HONEYPOT_SELECTOR);
    return honeypot instanceof HTMLInputElement && honeypot.value.trim() !== "";
  }

  function setConsentValidityState(consentInput, isInvalid) {
    const field = consentInput.closest(CONSENT_FIELD_SELECTOR);
    field?.classList.toggle("is-invalid", isInvalid);

    if (isInvalid) {
      consentInput.setAttribute("aria-invalid", "true");
    } else {
      consentInput.removeAttribute("aria-invalid");
    }
  }

  function getSectionId(form) {
    return (
      form.closest(".shopify-section")?.id?.replace(/^shopify-section-/, "") ||
      ""
    );
  }

  function getFormContext(form, emailInput) {
    return (
      form.dataset.klaviyoNewsletterContext ||
      form.getAttribute("id") ||
      emailInput.getAttribute("id") ||
      getSectionId(form) ||
      "newsletter-form"
    );
  }

  function buildProfileProperties(form, config, emailInput) {
    const properties = {
      custom_source: config?.customSource || "",
      newsletter_form_context: getFormContext(form, emailInput),
      newsletter_page_path: window.location.pathname,
      newsletter_page_title: document.title,
      shopify_page_type: config?.pageType || "",
      shopify_locale: config?.locale || "",
      shopify_section_id: getSectionId(form),
      shopify_customer_id: config?.customerId ? String(config.customerId) : "",
    };

    return Object.fromEntries(
      Object.entries(properties).filter(([, value]) => value),
    );
  }

  function buildPayload(form, config, emailInput) {
    return {
      data: {
        type: "subscription",
        attributes: {
          profile: {
            data: {
              type: "profile",
              attributes: {
                email: emailInput.value.trim(),
                properties: buildProfileProperties(form, config, emailInput),
              },
            },
          },
        },
        relationships: {
          list: {
            data: {
              type: "list",
              id: config.listId,
            },
          },
        },
      },
    };
  }

  function getSubmitButton(form) {
    const submitButton = form.querySelector(
      'button[type="submit"], input[type="submit"]',
    );
    return submitButton instanceof HTMLButtonElement ||
      submitButton instanceof HTMLInputElement
      ? submitButton
      : null;
  }

  function setSubmittingState(form, isSubmitting) {
    const submitButton = getSubmitButton(form);
    if (!submitButton) {
      return;
    }

    submitButton.disabled = isSubmitting;
    submitButton.setAttribute("aria-busy", String(isSubmitting));
  }

  function clearMessages(form, emailInput) {
    form
      .querySelectorAll(".newsletter-form__message")
      .forEach((messageElement) => messageElement.remove());
    emailInput.removeAttribute("aria-invalid");
    emailInput.removeAttribute("aria-describedby");
  }

  function showErrorMessage(form, emailInput, message) {
    const fieldWrapper =
      form.querySelector(".newsletter-form__field-wrapper") || form;
    const messageElement = document.createElement("small");
    const messageId = `KlaviyoNewsletter-error--${getFormContext(
      form,
      emailInput,
    )
      .replace(/[^a-zA-Z0-9_-]+/g, "-")
      .toLowerCase()}`;

    messageElement.className = "newsletter-form__message form__message";
    messageElement.id = messageId;
    messageElement.textContent = message;

    fieldWrapper.appendChild(messageElement);
    emailInput.setAttribute("aria-invalid", "true");
    emailInput.setAttribute("aria-describedby", messageId);
  }

  async function syncNewsletter(form, config, emailInput) {
    const response = await fetch(
      `https://a.klaviyo.com/client/subscriptions/?company_id=${encodeURIComponent(config.companyId)}`,
      {
        method: "POST",
        keepalive: true,
        headers: {
          Accept: "application/vnd.api+json",
          "Content-Type": "application/vnd.api+json",
          Revision: KLAVIYO_REVISION,
        },
        body: JSON.stringify(buildPayload(form, config, emailInput)),
      },
    );

    if (response.ok || response.status === 202) {
      return true;
    }

    try {
      const result = await response.json();
      console.error(
        "Klaviyo newsletter sync failed.",
        result?.errors?.[0]?.detail || result,
      );
    } catch (error) {
      console.error(
        "Klaviyo newsletter sync: failed to parse error response.",
        error,
      );
    }

    return false;
  }

  async function handleNewsletterSubmit(event) {
    const form = event.target;
    if (!isNewsletterForm(form)) {
      return;
    }

    if (hasBotFieldValue(form)) {
      event.preventDefault();
      return;
    }

    if (!form.checkValidity()) {
      event.preventDefault();
      return;
    }

    const config = getConfig();
    if (!isSyncEnabled(config)) {
      return;
    }

    const emailInput = getEmailInput(form);
    if (!emailInput) {
      return;
    }

    if (form.hasAttribute(SUBMITTING_ATTRIBUTE)) {
      event.preventDefault();
      return;
    }

    form.setAttribute(SUBMITTING_ATTRIBUTE, "true");
    setSubmittingState(form, true);

    void syncNewsletter(form, config, emailInput).finally(() => {
      form.removeAttribute(SUBMITTING_ATTRIBUTE);
    });
  }

  function handleInvalidField(event) {
    if (
      event.target instanceof HTMLInputElement &&
      event.target.matches(CONSENT_SELECTOR)
    ) {
      setConsentValidityState(event.target, true);
    }
  }

  function handleConsentChange(event) {
    if (
      event.target instanceof HTMLInputElement &&
      event.target.matches(CONSENT_SELECTOR)
    ) {
      setConsentValidityState(event.target, !event.target.checked);
    }
  }

  document.addEventListener("invalid", handleInvalidField, true);
  document.addEventListener("change", handleConsentChange);
  document.addEventListener("submit", handleNewsletterSubmit, true);
})();
